import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import type { FilterOption } from "../api/client";

interface FieldDef {
  key: string;
  label: string;
  default?: boolean;
}

interface FieldGroup {
  label: string;
  fields: FieldDef[];
}

const COMPANY_FIELD_GROUPS: FieldGroup[] = [
  {
    label: "Basic Info",
    fields: [
      { key: "name", label: "Company Name", default: true },
      { key: "legal_name", label: "Legal Name" },
      { key: "country", label: "Country", default: true },
      { key: "org_number", label: "Org Number", default: true },
      { key: "domain", label: "Domain", default: true },
      { key: "website", label: "Website", default: true },
      { key: "linkedin_url", label: "LinkedIn URL" },
    ],
  },
  {
    label: "Classification",
    fields: [
      { key: "industry", label: "Industry", default: true },
      { key: "industry_code", label: "Industry Code" },
      { key: "ai_industries", label: "AI Industries", default: true },
      { key: "ai_industries_text", label: "AI Industries (text)" },
      { key: "ai_served_verticals", label: "Served Verticals" },
      { key: "ai_served_verticals_text", label: "Served Verticals (text)" },
      { key: "org_form", label: "Org Form", default: true },
      { key: "description", label: "Description" },
      { key: "ai_summary", label: "AI Summary" },
    ],
  },
  {
    label: "Contact Info",
    fields: [
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "address_street", label: "Street" },
      { key: "address_city", label: "City" },
      { key: "address_postal_code", label: "Postal Code" },
      { key: "address_municipality", label: "Municipality" },
    ],
  },
  {
    label: "Size & Financials",
    fields: [
      { key: "employee_count", label: "Employee Count", default: true },
      { key: "employee_size_bucket", label: "Employee Size Bucket", default: true },
      { key: "revenue", label: "Revenue (your currency)", default: true },
      { key: "operating_result", label: "Operating Result (your currency)", default: true },
      { key: "reported_revenue", label: "Revenue as Reported", default: true },
      { key: "reported_operating_result", label: "Operating Result as Reported", default: true },
      { key: "reported_currency", label: "Reported Currency", default: true },
      { key: "latest_fiscal_year", label: "Latest Fiscal Year" },
    ],
  },
];

const PROSPECT_FIELDS: FieldDef[] = [
  { key: "added_by", label: "Added By", default: true },
  { key: "date_added", label: "Date Added", default: true },
  { key: "labels", label: "Labels", default: true },
  { key: "inclusion_reason", label: "Inclusion Reason", default: true },
];

// Mounted only while open, so each opening starts from the last saved selection.
interface ExportFieldsDialogProps {
  onClose: () => void;
  onExport: (fields: string[], roleColumns: string[]) => Promise<void>;
  storageKey: string;
  showProspectFields?: boolean;
  roleTypes?: FilterOption[];
}

export default function ExportFieldsDialog({
  onClose,
  onExport,
  storageKey,
  showProspectFields = false,
  roleTypes = [],
}: ExportFieldsDialogProps) {
  const allFields = useMemo(() => {
    const fields = COMPANY_FIELD_GROUPS.flatMap((g) => g.fields);
    if (showProspectFields) fields.push(...PROSPECT_FIELDS);
    return fields;
  }, [showProspectFields]);

  const defaultFieldKeys = useMemo(
    () => allFields.filter((f) => f.default).map((f) => f.key),
    [allFields],
  );

  const [stored] = useState<{ fields?: string[]; roles?: string[] }>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) ?? "{}");
    } catch {
      return {};
    }
  });
  const [selectedFields, setSelectedFields] = useState<Set<string>>(() => {
    if (!stored.fields?.length) return new Set(defaultFieldKeys);
    const known = new Set(allFields.map((f) => f.key));
    return new Set(stored.fields.filter((k) => known.has(k)));
  });
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(() => new Set(stored.roles ?? []));

  const toggleField = useCallback((key: string) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleRole = useCallback((code: string) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  const selectAllFields = useCallback(() => {
    setSelectedFields(new Set(allFields.map((f) => f.key)));
  }, [allFields]);

  const deselectAllFields = useCallback(() => {
    setSelectedFields(new Set());
  }, []);

  // The caller reports a failed export itself.
  const exportMutation = useMutation({
    mutationFn: ({ fields, roles }: { fields: string[]; roles: string[] }) => onExport(fields, roles),
    meta: { suppressErrorToast: true },
    onSuccess: onClose,
  });

  const handleExport = () => {
    const fields = Array.from(selectedFields);
    const roles = Array.from(selectedRoles);

    try {
      localStorage.setItem(storageKey, JSON.stringify({ fields, roles }));
    } catch {
      // ignore
    }

    exportMutation.mutate({ fields, roles });
  };

  const fieldGroups = showProspectFields
    ? [...COMPANY_FIELD_GROUPS, { label: "Prospect Info", fields: PROSPECT_FIELDS }]
    : COMPANY_FIELD_GROUPS;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export to CSV</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: 500 }}>
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <Button size="small" onClick={selectAllFields}>
            Select all
          </Button>
          <Button size="small" onClick={deselectAllFields}>
            Deselect all
          </Button>
        </Box>

        {fieldGroups.map((group) => (
          <Box key={group.label} sx={{ mb: 1.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
                mb: 0.5
              }}>
              {group.label}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0 }}>
              {group.fields.map((field) => (
                <FormControlLabel
                  key={field.key}
                  sx={{ width: "48%", mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedFields.has(field.key)}
                      onChange={() => toggleField(field.key)}
                    />
                  }
                  label={<Typography variant="body2">{field.label}</Typography>}
                />
              ))}
            </Box>
          </Box>
        ))}

        {roleTypes.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
                mb: 0.5
              }}>
              Role Columns (adds a column with the contact name for each selected role, max 5)
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0 }}>
              {roleTypes.map((rt) => (
                <FormControlLabel
                  key={rt.code}
                  sx={{ width: "48%", mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedRoles.has(rt.code)}
                      onChange={() => toggleRole(rt.code)}
                      disabled={!selectedRoles.has(rt.code) && selectedRoles.size >= 5}
                    />
                  }
                  label={<Typography variant="body2">{rt.label}</Typography>}
                />
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleExport}
          loading={exportMutation.isPending}
          disabled={selectedFields.size === 0}
        >
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
}
